import { selectWorkspace } from "@shared/slices/common.slice";
import { WorkspaceType } from "@shared/types";
import Flex from "common/Boxes/Flex";
import React, { useContext, useEffect, useMemo, useRef } from "react";
import { OnboardingStyles } from "features/onboarding/common/styles";
import { typography } from "@shared/common/Themes/typography";
import Title from "common/Typography/Title";
import styled, { useTheme } from "styled-components";
import useGroupOverview from "@shared/features/onboarding/hooks/useGroupOverview";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH, SIGNALS_BOT_PATH } from "common/constants";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import SkipOnboardingModal from "features/onboarding/common/SkipOnboardingModal";
import {
  displayResultMessage,
  getAnalyticsBotSource,
  startIntercomChecklist,
} from "utils/misc";
import { OnboardingContext } from "@shared/features/onboarding/Onboarding.context";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import {
  OnboardingStep,
  TradingMethodCategoryValueType,
  TradingMethodType as AnalyticsTradingMethodType,
} from "analytics/events";
import useQuery from "utils/hooks/useQuery";
import FreeTag from "features/onboarding/common/FreeTag";
import { getSkipOnboardingFollowGroupModalContent } from "@shared/features/onboarding/utils";
import SignalsBotOverview from "features/onboarding/overview/signalsGroup/SignalsBotOverview";
import { useSelector } from "react-redux";
import { BotFormType } from "@shared/features/bots/BotForm/common/types";
import { BotData, BotType, LanguageNameSpaces } from "@shared/types";
import BotFormProvider from "@shared/features/bots/BotForm/common/BotFormProvider";
import { sharedLib } from "@shared/services/shared.service";
import { useBottomNavigator } from "utils/hooks/misc";
import { NEW_BOT_ID_QUERY_PARAM } from "features/bots/common/constants.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

import { ONBOARDING_SELECTED_GROUP_NAME_ID } from "testIds";

interface GroupOverviewCardProps {
  onCreated?: () => void;
  tradingMethodCategory?: TradingMethodCategoryValueType;
}

const GroupOverviewCard = ({
  tradingMethodCategory,
  onCreated,
}: GroupOverviewCardProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  const theme = useTheme();
  const isDemo = useSelector(selectWorkspace) === WorkspaceType.DEMO;
  const { analytics } = useContext(AnalyticsContext);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  const query = useQuery();
  const queryGroupId = query.get("groupID");
  const groupRank = query.get("groupRank") ?? undefined;
  const parsedGroupRank =
    groupRank !== undefined ? parseInt(groupRank) : undefined;
  const bottomNavigatorData = useBottomNavigator();

  const { onFinishOnboarding, finishOnboardingLoading, changeStep } =
    useContext(OnboardingContext)!;

  const finishAndViewBot = (botData?: BotData) => {
    const queryToAppend = botData
      ? {
          [NEW_BOT_ID_QUERY_PARAM]: botData.id.toString(),
        }
      : undefined;
    onFinishOnboarding(SIGNALS_BOT_PATH, queryToAppend);
    if (!isDemo && !bottomNavigatorData.baseShouldDisplay) {
      startIntercomChecklist(27892459);
    }
  };

  const onFollowGroupFinishOnboarding = (botData?: BotData): void => {
    if (isDemo && botData?.id) {
      onCreated?.();
    }
    finishAndViewBot(botData);
  };

  const {
    isLoading,
    groupData,
    startAutoTradingLoading,
    skipOnboardingModalVisible,
    setSkipOnboardingModalVisible,
    baseAnalyticsProps,
    subscribeGroup,
    getConfirmMessage,
    overviewProps,
  } = useGroupOverview({
    queryGroupId,
    displayResultMessage,
    changeStep,
    onFinishOnboarding: onFollowGroupFinishOnboarding,
  });

  const botSource = useMemo(() => getAnalyticsBotSource(), []);
  const analyticsProps = {
    ...baseAnalyticsProps,
    tradingMethodCategory,
    onboardingStep: OnboardingStep.OVERVIEW,
    tradingMethodType: AnalyticsTradingMethodType.SIGNALS_GROUP,
    botSource,
    marketplaceGroupPagePosition: parsedGroupRank,
    botRank: undefined,
  };

  // Once the channel has loaded, so its name and exchange are known.
  const overviewSent = useRef(false);
  useEffect(() => {
    if (!analytics || isLoading || !groupData || overviewSent.current) return;
    overviewSent.current = true;
    analytics.onboardingOverviewView(analyticsProps);
  }, [analytics, isLoading, groupData]);

  const topSection = (
    <GroupWrapper>
      {groupData?.image && (
        <OnboardingStyles.Logo src={groupData.image} $small={true} />
      )}
      <div>
        <Title
          level={3}
          data-testid={ONBOARDING_SELECTED_GROUP_NAME_ID}
          $weight={typography.fontWeights.BOLD}
          style={{ paddingBottom: 4 }}
        >
          {groupData?.name}
        </Title>
        <FreeTag isFree={groupData?.is_free} />
      </div>
    </GroupWrapper>
  );

  const content = (
    <BotFormProvider
      botFormType={BotFormType.CREATE}
      botType={BotType.SIGNAL}
      loadingEndDelayMs={ONBOARDING_LOADING_END_DELAY_MS}
    >
      <SignalsBotOverview
        groupData={groupData}
        subscribeGroup={subscribeGroup}
        getConfirmMessage={getConfirmMessage}
        {...overviewProps}
      />
    </BotFormProvider>
  );

  const modal = (
    <SkipOnboardingModal
      isVisible={skipOnboardingModalVisible}
      content={getSkipOnboardingFollowGroupModalContent(user, t)}
      onCancel={() => setSkipOnboardingModalVisible(false)}
      newPath={"/"}
      onboardingStep={OnboardingStep.OVERVIEW}
      tradingMethodType={AnalyticsTradingMethodType.SIGNALS_GROUP}
    />
  );

  const loading =
    isLoading || startAutoTradingLoading || finishOnboardingLoading;

  return (
    <OnboardingStyles.OverviewCard $isMobile={isMobile}>
      <LoadingSpinnerOverlay
        isLoading={loading}
        hideContentWhileLoading={true}
        wrap={true}
        backgroundColor={theme.colors.cardBgColor}
        wrapStyle={{ minHeight: 500 }}
        spinnerSize="large"
        showContent={!isLoading}
      >
        {topSection}
        {content}
      </LoadingSpinnerOverlay>
      {modal}
    </OnboardingStyles.OverviewCard>
  );
};

export default GroupOverviewCard;

const ONBOARDING_LOADING_END_DELAY_MS = 200;

const GroupWrapper = styled(Flex)`
  justify-content: start;
  margin-bottom: 32px;
`;
